import { productApi } from "#/api/productApi";
import {
  showErrorNotification,
  showSuccessNotification,
} from "#/components/notifications";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { usePermission } from "#/hooks/usePermission";
import type { Product } from "#/types/products";
import { Button, Modal, Text } from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

interface BulkDeleteCategoriesModalProps {
  opened: boolean;
  onClose: () => void;
  selectedProducts: Product[];
  onSuccess: () => void;
}

const BulkDeleteCategoriesModal = ({
  opened,
  onClose,
  selectedProducts,
  onSuccess,
}: BulkDeleteCategoriesModalProps) => {
  const { t } = useTranslation();
  const { canDelete } = usePermission();

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: number[]) => {
      const response = await productApi.bulkDelete(ids);
      if (!response.data) {
        throw new Error("Failed to delete categories");
      }
      return response.data;
    },
    onSuccess: (data) => {
      showSuccessNotification({
        message: t("products.bulkDelete.success", { count: data.deleted }),
      });
      onClose();
      onSuccess();
    },
    onError: (error: unknown) => {
      const errorMessage =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || t("products.bulkDelete.error");
      showErrorNotification({
        message: errorMessage,
      });
    },
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      title={t("products.bulkDelete.title")}
      closeOnClickOutside={false}
      size="xl"
      classNames={{
        title: "font-bold text-lg",
      }}
    >
      <div className="w-full flex flex-col gap-3">
        <Text size="sm" c="dimmed">
          {t("products.bulkDelete.description", {
            count: selectedProducts.length,
          })}
        </Text>

        <div className="flex flex-col gap-2 max-h-[400px] overflow-y-auto">
          {selectedProducts.map((product) => {
            const itemCount = product.number_of_items ?? 0;
            const siteCount = product.number_of_sites ?? 0;
            const hasItems = itemCount > 0;

            return (
              <div
                key={product.id}
                className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3"
              >
                <div className="flex items-baseline justify-between">
                  <Text size="sm" fw={600}>{product.name}</Text>
                  {product.sku && (
                    <Text size="xs" c="dimmed">{product.sku}</Text>
                  )}
                </div>
                {hasItems ? (
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5">
                    <Text size="xs" c="dark">• {itemCount} {t("products.bulkDelete.items")}</Text>
                    <Text size="xs" c="dark">• {siteCount} {t("products.bulkDelete.sites")}</Text>
                    <Text size="xs" c="dimmed">• {t("products.bulkDelete.scanHistoryDeleted")}</Text>
                    <Text size="xs" c="dimmed">• {t("products.bulkDelete.rfidUnlinked")}</Text>
                    <Text size="xs" c="dimmed">• {t("products.bulkDelete.ticketsUnlinked")}</Text>
                    <Text size="xs" c="dimmed">• {t("products.bulkDelete.shipmentsUnlinked")}</Text>
                  </div>
                ) : (
                  <Text size="xs" c="dimmed" mt={2}>{t("products.bulkDelete.noItems")}</Text>
                )}
              </div>
            );
          })}
        </div>

        <Text size="xs" fw={500} c="orange.7" className="text-center">
          {t("products.bulkDelete.warning")}
        </Text>

        <div className="flex items-center justify-end gap-3">
          <Button
            variant="default"
            onClick={onClose}
            disabled={bulkDeleteMutation.isPending}
          >
            {t("common.cancel")}
          </Button>
          <PermissionTooltip disabled={!canDelete()}>
            <Button
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={() =>
                bulkDeleteMutation.mutate(
                  selectedProducts.map((p) => p.id),
                )
              }
              loading={bulkDeleteMutation.isPending}
              disabled={!canDelete()}
            >
              {t("products.bulkDelete.confirmButton", {
                count: selectedProducts.length,
              })}
            </Button>
          </PermissionTooltip>
        </div>
      </div>
    </Modal>
  );
};

export default BulkDeleteCategoriesModal;
